import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Link as MUILink,
  TableSortLabel,
  Box,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import React, { useEffect, useState } from "react";
import RelatedInput from "models/input/related-input";
import SolrService from "services/SolrService";
import SearchOptions from "datalign-module-template/models/search-options";
import AlternateSearchInput from "datalign-module-template/components/search-input/AlternateSearchInput";
import InputField from "models/input/input-field";
import { useEasierTheme } from "datalign-themes";
import { HeadCell } from "datalign-module-template/models/table-header-generic";
import { visuallyHidden } from "@mui/utils";
// import { HeadCell } from "datalign-module-template/models/table-header-generic";

const { inputService } = new SolrService();

type RelatedTo = { id: string; title: string };

const headCells: HeadCell<RelatedTo>[] = [
  { disablePadding: false, id: "id", label: "Id", sort: true },
  { disablePadding: false, id: "title", label: "Title", sort: true },
];

const RelatedField = ({ field }: { field: InputField }) => {
  const theme = useEasierTheme();

  const [count, setCount] = useState<number>();
  const [related, setRelated] = useState<RelatedInput[]>([]);
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    page_rows: 10,
    page_num: 0,
    order_by: {
      column_name: "id",
      order: "desc",
    },
    search_term: "",
  });
  useEffect(() => {
    inputService.get_related(field.data, searchOptions).then((res) => {
      if (res) {
        setCount(res.count);
        setRelated(res.data);
      }
    });
  }, [field.data, searchOptions]);

  const createSortHandler =
    (property: keyof RelatedTo) => (_e: React.MouseEvent<unknown>) => {
      const isAsc =
        searchOptions.order_by.column_name === property &&
        searchOptions.order_by.order === "asc";
      setSearchOptions({
        ...searchOptions,
        order_by: {
          column_name: String(property),
          order: isAsc ? "desc" : "asc",
        },
      });
    };

  return (
    <TableContainer component={Paper}>
      <AlternateSearchInput
        value={searchOptions.search_term}
        onChange={(val) =>
          setSearchOptions({ ...searchOptions, search_term: val })
        }
        boxProps={{ style: { margin: "4px" } }}
      />
      <Table>
        <TableHead>
          <TableRow>
            {headCells.map((headCell) => (
              <TableCell
                key={String(headCell.id)}
                align={"left"}
                padding={headCell.disablePadding ? "none" : "normal"}
                sortDirection={
                  headCell.sort
                    ? searchOptions.order_by.column_name === headCell.id &&
                      headCell.sort
                      ? searchOptions.order_by.order === "desc"
                        ? "desc"
                        : "asc"
                      : false
                    : false
                }
                sx={
                  ["nrdata-orig", "FAF-nt"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? {
                        border: "1px solid #e6e6e6",
                      }
                    : undefined
                }
              >
                {headCell.sort ? (
                  <TableSortLabel
                    active={
                      searchOptions.order_by.column_name === headCell.id &&
                      headCell.sort
                    }
                    direction={
                      searchOptions.order_by.column_name === headCell.id
                        ? searchOptions.order_by.order === "desc"
                          ? "desc"
                          : "asc"
                        : "asc"
                    }
                    onClick={createSortHandler(headCell.id)}
                  >
                    {headCell.label}
                    {searchOptions.order_by.column_name === headCell.id &&
                      headCell.sort && (
                        <Box component="span" sx={visuallyHidden}>
                          {searchOptions.order_by.order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      )}
                  </TableSortLabel>
                ) : (
                  headCell.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {related.map((row, index) => (
            <TableRow
              key={`table-row-${index}`}
              sx={
                ["nrdata-orig", "FAF-nt"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? {
                      boxShadow:
                        (index - 1) % 2
                          ? "inset 0 0 0 9999px rgba(0, 0, 0, 0.05);"
                          : undefined,
                    }
                  : undefined
              }
            >
              <TableCell
                sx={
                  ["nrdata-orig", "FAF-nt"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? {
                        border: "1px solid #e6e6e6",
                      }
                    : undefined
                }
              >
                <MUILink
                  to={`/view/${row.id}`}
                  component={RouterLink}
                  underline="hover"
                  mr={1}
                  sx={{
                    wordWrap: "break-word",
                    fontWeight: 400,
                    color: ["nrdata-orig", "FAF-nt"].includes(
                      process.env.REACT_APP_STYLING_THEME ?? "",
                    )
                      ? "#06c"
                      : undefined,
                    fontSize: "1rem",
                  }}
                >
                  {row.id}
                </MUILink>
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "1rem",
                  border: ["nrdata-orig", "FAF-nt"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? "1px solid #e6e6e6"
                    : undefined,
                }}
              >
                {row.title}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={count ?? 0}
        rowsPerPage={searchOptions.page_rows}
        page={searchOptions.page_num}
        onPageChange={(_event, newPage) =>
          setSearchOptions({ ...searchOptions, page_num: newPage })
        }
        onRowsPerPageChange={(event) =>
          setSearchOptions({
            ...searchOptions,
            page_num: 0,
            page_rows: parseInt(event.target.value ?? 10),
          })
        }
      />
    </TableContainer>
  );
};

export default RelatedField;
